import { useEffect, useRef, useState, type ReactNode } from 'react'
import { NavLink } from 'react-router-dom'
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface SiteHeaderNavChild {
  to: string
  label: string
  description?: string
}

export interface SiteHeaderNavItem {
  to: string
  label: string
  /** Dropdown cards shown on hover */
  children?: SiteHeaderNavChild[]
  /** Exact-match active styling — use when this route is a path prefix */
  end?: boolean
}

interface SiteHeaderProps {
  logo?: ReactNode
  navItems?: SiteHeaderNavItem[]
  rightSlot?: ReactNode
  /**
   * Rendered inside <header> after the main bar and dropdown drawer.
   * Use this to inject a mobile menu without breaking the sticky block.
   */
  belowSlot?: ReactNode
  className?: string
}

// ─── Component ─────────────────────────────────────────────────────────────────

export function SiteHeader({
  logo,
  navItems,
  rightSlot,
  belowSlot,
  className,
}: SiteHeaderProps) {
  const [activeMenu, setActiveMenu] = useState<string | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const activeChildren = activeMenu
    ? navItems?.find((item) => item.to === activeMenu)?.children
    : undefined
  const isMenuOpen = !!(activeChildren?.length)

  const hasDropdowns = navItems?.some((item) => item.children?.length)

  function cancelClose() {
    if (closeTimer.current) clearTimeout(closeTimer.current)
  }

  function scheduleClose() {
    closeTimer.current = setTimeout(() => setActiveMenu(null), 100)
  }

  return (
    <header className={cn('border-b bg-background', className)}>

      {/* ── Main bar ──────────────────────────────────────────────────────────── */}
      <div className="container mx-auto flex h-16 items-center justify-between px-4">
        <div className="flex items-center gap-6">
          {logo}

          {navItems && navItems.length > 0 && (
            <nav className="hidden h-16 items-stretch gap-1 xl:flex">
              {navItems.map(({ to, label, children, end }) => (
                <div
                  key={to}
                  className="flex items-center"
                  onMouseEnter={() => {
                    cancelClose()
                    setActiveMenu(children?.length ? to : null)
                  }}
                  onMouseLeave={scheduleClose}
                >
                  <NavLink
                    to={to}
                    end={end}
                    className={({ isActive }) =>
                      cn(
                        'inline-flex h-8 items-center rounded-md px-3 text-sm font-medium transition-colors focus-visible:outline-none',
                        isActive
                          ? 'bg-accent text-accent-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                        activeMenu === to && !isActive && 'text-foreground'
                      )
                    }
                  >
                    {label}
                    {children?.length && (
                      <ChevronDown
                        className={cn(
                          'ml-1 h-3.5 w-3.5 transition-transform duration-200',
                          activeMenu === to && 'rotate-180'
                        )}
                      />
                    )}
                  </NavLink>
                </div>
              ))}
            </nav>
          )}
        </div>

        {rightSlot && (
          <div className="flex items-center gap-3">{rightSlot}</div>
        )}
      </div>

      {/* ── Dropdown mega-menu drawer (desktop only) ───────────────────────────
          Uses grid-template-rows 0fr→1fr for smooth, jank-free height animation.
          Only rendered when at least one nav item has children.
      ─────────────────────────────────────────────────────────────────────────── */}
      {hasDropdowns && (
        <div
          className="hidden xl:grid"
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          style={{
            gridTemplateRows: isMenuOpen ? '1fr' : '0fr',
            transition: 'grid-template-rows 200ms ease-out',
          }}
        >
          <div className="overflow-hidden">
            <div
              className="border-t"
              style={{
                opacity: isMenuOpen ? 1 : 0,
                transform: isMenuOpen ? 'translateY(0)' : 'translateY(-6px)',
                transition: 'opacity 200ms ease-out, transform 200ms ease-out',
              }}
            >
              <div className="container mx-auto px-4 py-3">
                <ScrollableRow>
                  {activeChildren?.map((child) => (
                    <NavLink
                      key={child.to}
                      to={child.to}
                      onClick={() => setActiveMenu(null)}
                      className={({ isActive }) =>
                        cn(
                          'min-w-[180px] shrink-0 rounded-lg px-4 py-3 transition-colors hover:bg-accent',
                          isActive && 'bg-accent'
                        )
                      }
                    >
                      <div className="text-sm font-medium">{child.label}</div>
                      {child.description && (
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {child.description}
                        </div>
                      )}
                    </NavLink>
                  ))}
                </ScrollableRow>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Below slot (e.g. mobile nav) ──────────────────────────────────────── */}
      {belowSlot}
    </header>
  )
}

// ─── ScrollableRow ───────────────────────────────────────────────────────────
// Horizontal scroller that hides the native scrollbar and shows chevron
// affordances only when overflow exists in that direction.

function ScrollableRow({ children }: { children: ReactNode }) {
  const scrollerRef = useRef<HTMLDivElement>(null)
  const [canLeft, setCanLeft] = useState(false)
  const [canRight, setCanRight] = useState(false)

  function update() {
    const el = scrollerRef.current
    if (!el) return
    setCanLeft(el.scrollLeft > 1)
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }

  useEffect(() => {
    const el = scrollerRef.current
    if (!el) return
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    for (const c of Array.from(el.children)) ro.observe(c)
    return () => ro.disconnect()
  }, [children])

  function scrollBy(delta: number) {
    scrollerRef.current?.scrollBy({ left: delta, behavior: 'smooth' })
  }

  return (
    <div className="relative">
      <div
        ref={scrollerRef}
        onScroll={update}
        className="flex gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {children}
      </div>

      {canLeft && (
        <button
          type="button"
          aria-label="Scroll left"
          onClick={() => scrollBy(-240)}
          className="absolute left-0 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-full border bg-background/95 shadow-sm hover:bg-accent"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      )}
      {canRight && (
        <button
          type="button"
          aria-label="Scroll right"
          onClick={() => scrollBy(240)}
          className="absolute right-0 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-full border bg-background/95 shadow-sm hover:bg-accent"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}
